import {
  Authority,
  DeleteModal,
  Loading,
  QUERIES,
  STRIPE_TOKEN,
  deletePaymentCard,
  getInvoicesData,
  getPaymentCards,
  openNotification,
  scrollBarStyles,
  setDefaultPaymentCard,
  useAppLayout,
  useDocumentTitle,
} from "@dataone/lib";

import { Breadcrumb, Image, Button, Modal, Table, Space } from "antd";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { useMutation, useQuery } from "@tanstack/react-query";
import CheckoutForm from "../../auth/components/CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  IPaymentCard,
  ITransactionInvoice,
} from "@dataone/lib/interfaces/payment.interface";
import UpdateModalCard from "../components/UpdateModalCard";
import { useAuth } from "../../auth/contexts/auth.provider";
import {
  CreditCardIcon,
  ExportIcon,
  MasterCardIcon,
  NoCardIcon,
  PointingArrowIcon,
  TrashIcon,
  VisaCardIcon,
} from "@dataone/icons";
import { withAuth } from "../../auth/helpers/hoc";
import { QuoteRequestIcon } from "@dataone/icons";

interface BillingMethodePageProps {
  children?: React.ReactNode;
}

const stripePromise = loadStripe(STRIPE_TOKEN);

const BillingMethodePage = (props: BillingMethodePageProps) => {
  const [isOpenModalAddCard, setIsOpenModalAddCard] = useState(false);

  useDocumentTitle("OneGate | Billing Settings");
  const navigate = useNavigate();
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: cardDelete } = useMutation(deletePaymentCard);
  const { mutate: setDefaultPayment } = useMutation(setDefaultPaymentCard);

  const [cardToDelete, setCardToDelete] = useState<IPaymentCard | null>(null);

  const [cardInfo, setCardInfo] = useState<IPaymentCard | null>(null);

  const { user } = useAuth();

  const hasInvoicesSection =
    user?.groups[0] &&
    [Authority.OG_SUBSCRIBER, Authority.OG_SUBSCRIBER_EX].includes(
      user?.groups[0]
    );

  const {
    data: cards,
    isLoading,
    refetch: refetchCards,
  } = useQuery({
    queryKey: [QUERIES.PAYMENT_CARDS],
    queryFn: getPaymentCards,
  });

  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: [QUERIES.INVOICES],
    enabled: hasInvoicesSection,
    queryFn: getInvoicesData,
  });

  const handleCancelDelete = () => {
    setCardToDelete(null);
  };

  const handleCancelAddCard = () => {
    setIsOpenModalAddCard(false);
  };

  const handleDelete = (card: IPaymentCard) => {
    setCardToDelete(card);
  };

  const handleSetDefaultCard = () => {
    setUpdateLoading(true);
    cardInfo &&
      setDefaultPayment(cardInfo?.id, {
        onSuccess: ({ message }) => {
          openNotification(message);
          setCardInfo(null);
          setIsOpen(false);
          refetchCards();
          setUpdateLoading(false);
        },
      });
  };

  const handleConfirmDelete = () => {
    cardToDelete &&
      cardDelete(cardToDelete.id, {
        onSuccess: ({ message }) => {
          openNotification(message);
          setCardToDelete(null);
          refetchCards();
        },
      });
  };

  const handleDownload = (invoicePdf: string | URL | undefined) => {
    if (invoicePdf) {
      const link = document.createElement("a");
      link.href = invoicePdf.toString();
      link.download = "invoice.pdf";
      link.click();
    } else {
      console.error("Invoice PDF URL is undefined");
    }
  };

  const columns = [
    {
      title: "Billing Date",
      dataIndex: "effectiveAt",
      key: "effectiveAt",
      render: (text: string) => {
        const date = new Date(text);
        return date.toLocaleDateString("en-US");
      },
    },
    {
      title: "Title",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Amount",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (text: number, record: ITransactionInvoice) => {
        const currencySymbol =
          record.currency === "eur"
            ? "$"
            : record.currency === "dol"
            ? "$"
            : "";
        return `${text}  ${currencySymbol} `;
      },
    },
    {
      title: "",
      dataIndex: "invoicePdf",
      key: "invoicePdf",
      render: (text: string, record: ITransactionInvoice) => (
        <Space size="middle">
          <div>
            <StyledUploadButton
              icon={
                <div
                  className="span"
                  onClick={() => handleDownload(record.invoicePdf)}
                >
                  <ExportIcon />
                </div>
              }
            ></StyledUploadButton>
          </div>
        </Space>
      ),
    },
  ];

  const { isSmartSearchVisible } = useAppLayout();

  useEffect(() => {
    if (isSmartSearchVisible) handleCancelAddCard();
  }, [isSmartSearchVisible]);

  const subscription = user?.subscription;

  return (
    <StyledBillingMethodePage id="billing-page">
      <h2>Billing settings</h2>
      <StyledContent>
        <StyledDescription>
          <StyledTitles>
            <StyledCreditCradIcon>
              <CreditCardIcon />
            </StyledCreditCradIcon>
            <p> Credit card(s) </p>
          </StyledTitles>
          <div style={{ fontSize: "13px", marginBottom: "20px" }}>
            Manage your credit cards and payment methods
          </div>
        </StyledDescription>
        <StyledCards>
          {subscription && (
            <div className="first-card">
              <StyledPremiumTitle>
                <div>{subscription.title}</div> <div>{subscription.type}</div>
              </StyledPremiumTitle>
              <p>Access and analyze with your internal data.</p>
              <div className="expiration">
                {"Expires on " +
                  new Date(subscription.currentPeriodEnd)
                    .toISOString()
                    .split("T")[0] +
                  " at " +
                  new Date(subscription.currentPeriodEnd)
                    .toISOString()
                    .split("T")[1]
                    .split(":")
                    .slice(0, 2)
                    .join(":")}
              </div>
              <div
                className="upgrade"
                onClick={() => {
                  navigate("/subscription");
                }}
              >
                Upgrade <PointingArrowIcon />
              </div>
            </div>
          )}
          <div className="second-card">
            <StyledDescription>
              <StyledTitles>
                <p> Payment Method </p>
              </StyledTitles>
              <div style={{ fontSize: "13px", marginBottom: "20px" }}>
                Manage your credit cards and payment methods
              </div>
              <StyledButton
                size="large"
                htmlType="submit"
                onClick={() => {
                  setIsOpenModalAddCard(true);
                }}
              >
                Add
              </StyledButton>
            </StyledDescription>
            <div className="cards-list">
              {isLoading ? (
                <Loading />
              ) : cards?.length !== 0 ? (
                cards?.map((card) => (
                  <>
                    <StyledCard
                      key={card.id}
                      className={
                        card.defaultCard !== true ? "isNotDefault" : "isDefault"
                      }
                    >
                      <StyledInfoCard>
                        {card.brand === "visa" ? (
                          <VisaCardIcon />
                        ) : (
                          <MasterCardIcon />
                        )}
                        <div>
                          <h4 style={{ margin: "0" }}>
                            {card.brand === "visa" ? "Visa" : "Mastercard"}{" "}
                            ending in {card.last4}{" "}
                          </h4>
                          <p style={{ margin: "0" }}>
                            Exp. dat {card.expMonth}/{card.expYear % 100}
                          </p>
                        </div>
                      </StyledInfoCard>
                      <StyledSettingCard>
                        {card.defaultCard === true ? (
                          <StyledDefaultCard>Default</StyledDefaultCard>
                        ) : (
                          <StyledDefaultButton
                            onClick={() => {
                              setIsOpen(true);
                              setCardInfo(card);
                            }}
                          >
                            {isLoading
                              ? " Setting as Default ..."
                              : "Set as Default"}
                          </StyledDefaultButton>
                        )}
                        <StyledButtonImage
                          onClick={() => {
                            handleDelete(card);
                          }}
                        >
                          <TrashIcon />
                        </StyledButtonImage>
                      </StyledSettingCard>
                    </StyledCard>
                  </>
                ))
              ) : (
                <StyledEmptyCards style={{ marginTop: "10px" }}>
                  <div className="card-icon">
                    <NoCardIcon />
                  </div>
                  <StyledEmptyCardsContent>
                    <h3 style={{ fontSize: "20px" }}>No credit cards</h3>
                    <p style={{ fontSize: "16px" }}>
                      Please add credit card to your account
                    </p>
                  </StyledEmptyCardsContent>
                </StyledEmptyCards>
              )}
            </div>
          </div>
        </StyledCards>
        <DeleteModal
          header="Are you sure you want
          to delete this payment card ?"
          containerId="#billing-page"
          typeToDelete="Card"
          isModalOpen={!!cardToDelete}
          selectedTitle={`${
            cardToDelete?.brand === "visa" ? "Visa" : "Mastercard"
          } ending in ${cardToDelete?.last4}`}
          handleCancel={handleCancelDelete}
          handleConfirm={handleConfirmDelete}
        />
        <UpdateModalCard
          title="Update Default Card"
          subTitle="This credit card will be used by default for billing"
          containerId="#billing-page"
          isOpen={isOpen}
          cancelFunction={() => {
            setIsOpen(false);
            setUpdateLoading(false);
          }}
          updateFunction={handleSetDefaultCard}
          last4_card={cardInfo?.last4}
          updateLoading={updateLoading}
        />
        <StyledStripeModal
          title="Add new credit card"
          getContainer="#billing-page"
          open={isOpenModalAddCard}
          onCancel={handleCancelAddCard}
          width={600}
          destroyOnClose
          footer={null}
          styles={{
            mask: {
              position: "absolute",
              borderRadius: "20px 40px 40px 40px",
            },
          }}
          wrapClassName="absolute-modal-wrap"
        >
          <div style={{ width: "100%" }}>
            <Elements stripe={stripePromise}>
              <CheckoutForm
                refetchCards={refetchCards}
                handleCancel={handleCancelAddCard}
              />
            </Elements>
          </div>
        </StyledStripeModal>
      </StyledContent>
      {user?.groups[0] !== Authority.OG_GUEST && (
        <StyledIncreaseContent>
          <StyledIncreaseDescription>
            <StyledTitles>
              <QuoteRequestIcon />
              <p> Transaction & Invoices </p>
            </StyledTitles>
            <div>See last transaction & Invoice here</div>
          </StyledIncreaseDescription>
          <StyledForm>
            {isLoadingInvoices && hasInvoicesSection ? (
              <Loading />
            ) : (
              <Table
                // pagination={false}
                dataSource={invoicesData ? invoicesData?.content : []}
                columns={columns}
                pagination={{ defaultPageSize: 10 }}
              />
            )}
          </StyledForm>
        </StyledIncreaseContent>
      )}
    </StyledBillingMethodePage>
  );
};

export default withAuth(BillingMethodePage);

const StyledStripeModal = styled(Modal)`
  .ant-modal-content {
    padding: 15px 35px;
  }
  .ant-modal-header,
  .ant-modal-title {
    color: #4974f6 !important;
    font-weight: bold !important;
    font-size: 18px !important;
    word-wrap: break-word !important;
    margin-bottom: 15px !important;
  }
`;

const StyledUploadButton = styled(Button)`
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  .span {
    display: flex;
    color: #4974f6;
    font-size: 20px;
  }
`;

const StyledEmptyCardsContent = styled.div`
  color: #040726;
  h3 {
    font-size: 26px;
    font-weight: bold;
    margin-top: 0px;
  }
  p {
    font-size: 20px;
    font-weight: 300;
  }
`;

const StyledEmptyCards = styled.div`
  text-align: center;
  .card-icon {
    // font-size: 80px;
    font-size: 50px;
  }
`;

const StyledButtonImage = styled.button`
  border: 0;
  background-color: transparent;
  cursor: pointer;
  svg {
    font-size: 20px;
    color: #1c1b1f;
  }
`;

const StyledDefaultButton = styled.label`
  border-radius: 15px;
  background: transparent;
  color: #4974f6;
  font-weight: bold;
  cursor: pointer;
`;

const StyledDefaultCard = styled.label`
  border-radius: 15px;
  background: #4974f6;
  color: white;
  font-weight: bold;
  padding: 5px 10px;
  text-align: center;
`;

const StyledSettingCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2em;
  cursor: pointer;
`;

const StyledInfoCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2em;
  svg {
    font-size: 50px;
  }
`;

const StyledCard = styled.div`
  margin: auto 0;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  padding: 13px;
  margin-bottom: 5px;
  &.isDefault {
    background-color: #eff1fd !important;
  }
`;

const StyledBillingMethodePage = styled.div`
  width: 100%;
  padding: 15px 0px 0px 30px;
  h2 {
    color: #4974f6;
    font-weight: 700;
    font-size: 18px;
    margin-top: 2px;
    margin-bottom: 40px;
  }
  overflow-y: scroll;
  padding-bottom: 20px;
  height: calc(100vh - 80px);
  overflow-y: auto;
  ${scrollBarStyles}
  .ant-breadcrumb li:nth-of-type(1) a {
    font-size: 16px !important;
    color: #040726 !important;
    font-weight: 200 !important;
  }
  .ant-breadcrumb a {
    color: #4974f6;
    transition: color 0.2s;
    padding: 0 4px;
    border-radius: 4px;
    display: inline-block;
    margin-inline: -4px;
    font-size: 18px;
    font-weight: bold;
    background-color: transparent;
  }
`;

const StyledTitles = styled.div`
  display: flex;
  font-size: 12px;
  font-weight: bold;
  align-items: center;
  gap: 1.5rem;
  p {
    font-size: 16px;
  }
  svg {
    font-size: 30px;
    color: #4974f6;
  }
`;

const StyledForm = styled.div`
  border-radius: 20px;
  margin-right: 10px;
  // min-height: 250px;
  // min-width: 100%;
  .ant-empty-normal {
    margin-block: 0px !important;
  }
`;

const StyledIncreaseDescription = styled.div`
  div {
    font-size: 13px;
  }
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledDescription = styled.div`
  width: 100%;
`;

const StyledIncreaseContent = styled.div`
  .ant-table-thead .ant-table-cell {
    background-color: #eff1fd !important;
    text-align: center;
  }
  .table-rows td {
    background-color: #fff !important ;
  }
  .ant-table-cell::before {
    display: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    transition: background 0.2s, border-color 0.2s;
    border-bottom: 1px solid #f0f0f0;
    text-align: center;
  }
`;

const StyledContent = styled.div``;

const StyledButton = styled(Button)`
  background-color: #4974f6;
  color: #ffffff;
  border: none;
  float: right;
  margin-top: -73px;
  font-size: 14px !important;
  width: 60px;
  // height: 35px !important;
  &:hover {
    background: #4974f6 !important;
    color: #ffffff !important;
  }
`;

const StyledCreditCradIcon = styled.div`
  color: #4974f6;
  font-size: 20px;
  display: flex;
`;

const StyledCards = styled.div`
  display: flex;
  .first-card {
    border-radius: 20px;
    background: #eff1fd;
    height: 250px;
    min-width: 40%;
    margin-right: 10px;
    .ant-empty-normal {
      margin-block: 0px !important;
    }
    p {
      color: #5c7493;
      font-size: 12px;
      padding: 10px 0px 0px 20px;
    }
    .expiration {
      padding: 10px 0px 0px 20px;
      color: #000;
      font-weight: 700;
    }
    .upgrade {
      cursor: pointer;
      color: #4974f6;
      font-weight: 700;
      justify-content: end;
      display: flex;
      margin-top: 75px;
      margin-right: 10px;
      svg {
        font-size: 20px;
        margin-left: 10px;
      }
    }
  }
  .second-card {
    border-radius: 20px;
    margin-right: 10px;
    border: 2px solid #eff1fd;
    padding: 0px 15px;
    height: 250px;
    min-width: 58%;
    .ant-empty-normal {
      margin-block: 0px !important;
    }
    .cards-list {
      overflow-y: auto;
      height: 157px;
      &::-webkit-scrollbar {
        width: 0px;
      }
    }
  }
`;

const StyledPremiumTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-image: url("images/usage/package-background.svg");
  background-repeat: no-repeat;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  margin: 10px;
  padding: 10px;
  border-radius: 13px;
  height: 50px;
`;
